/**
 * `sibyl post <pr>` — the wiring that carries an accepted finding to GitHub, and nothing else.
 *
 * ⛔ THIS FILE MAKES NO POSTING JUDGEMENTS. `src/github/post.ts` owns every rule about what may be
 * sent — the locks, the caps, the anchoring, the mention guard, the commit status. This file
 * resolves a credential, rebuilds the anchor set, reads the triage queue, and REPORTS. A condition
 * written here would be a rule with no test, and worse, a second opinion about what may reach a
 * shared repository.
 *
 * ⛔ THE ANCHOR SET IS REBUILT FROM THE PULL REQUEST'S OWN FILES, NEVER REPLAYED FROM THE REVIEW.
 * The head may have moved since a person accepted the finding, and anchoring is all-or-nothing: one
 * comment on a line outside the CURRENT diff fails the whole `POST /pulls/{n}/reviews` call with a
 * 422 and nothing lands — not the other nineteen comments, not the summary. A stored anchor set is
 * a statement about a diff that no longer exists.
 *
 * ⛔ A REFUSAL AND A SILENT NO-OP MUST NOT LOOK THE SAME. Every engaged lock is printed before
 * anything else, with the exact setting that releases it, and the run exits 0 — it did what it was
 * built to do. `triage.require_human` defaults true, so the default run refuses, loudly.
 *
 * ⛔ A RUN THAT POSTS A BLOCKING REVIEW EXITS 0. The exit code answers "did the harness work", never
 * "was the code clean" — see the header of src/cli/index.ts for the defect that rule comes from.
 *
 * ⛔ NOTHING IS DROPPED SILENTLY. A person accepted each of these findings and is entitled to know
 * which ones did not go and why. The report reconciles: accepted = posted + excluded + withheld, and
 * says so out loud when the arithmetic does not balance.
 */

import type { SibylConfig } from "../config/schema.js"
import { EXIT } from "../config/validate.js"
import {
  buildAnchorSet,
  GitHub,
  isAnchorable,
  wasIntroducedHere,
  type AnchorSet,
  type ChangedFile,
} from "../github/pr.js"
import {
  buildReview,
  engagedLocks,
  postReview,
  PostError,
  type EngagedLock,
  type LockId,
  type PostableFinding,
  type PostConfig,
  type ReviewPlan,
} from "../github/post.js"
import { openTriageStore, type StoreBackendInfo, type TriageRecord } from "../triage/store.js"
import type { Finding, FindingClass } from "../types/finding.js"
// ⚠️ The queue's own abbreviation, not a second one. It keeps the rule readable and stays long
// enough to paste straight into `sibyl triage accept`, which resolves a prefix.
import { shortFingerprint } from "./queue.js"
import { credentialFrom } from "./review.js"
import { licenceRefusal } from "../licence/gate.js"

/**
 * What this command needs from the triage store.
 *
 * ⚠️ Structural, so a test runs against a queue it built rather than whatever is in the developer's
 * real database — the same reason `SqliteDatabaseLike` exists in src/triage/store.ts.
 */
export interface PostStore {
  readonly backend: StoreBackendInfo
  all(): TriageRecord[]
  close(): void
}

export interface PostCommandOptions {
  readonly repo?: string
  readonly pr: number
  readonly config: SibylConfig
  readonly env?: NodeJS.ProcessEnv
  /** Everything after `post`, so this file owns its own flags. */
  readonly argv?: readonly string[]
  readonly out?: (s: string) => void
  readonly err?: (s: string) => void
  /** ⛔ Injected in every test. No test in this repository reaches the network. */
  readonly fetchImpl?: typeof fetch
  readonly openStore?: (path: string) => Promise<PostStore>
}

/**
 * The setting that releases each lock, in the words of the file a person has to edit.
 *
 * ⛔ Naming the lock without naming its release is how a tool teaches people to turn everything off.
 * `triage.require_human` is deliberately NOT presented as a switch to flip: the release is to make
 * the decision, because "a review is drafted for a person, who decides whether it is posted" is the
 * product constraint this whole tool exists to express (docs/spec/constraints.md).
 */
export const LOCK_RELEASES: Record<LockId, string> = {
  "post.enabled": "set `enabled = true` under [post] in sibyl.toml",
  "post.dry_run": "set `dry_run = false` under [post], and drop `--dry-run`",
  "triage.require_human":
    "decide the findings named above with `sibyl triage accept <fp>` or `sibyl triage dismiss <fp> --reason <r>`",
  "post.confirm_token": "pass `--confirm <token>` matching [post].confirm_token literally",
}

// ── A stored decision, read back as a finding ──────────────────────────────────────────────────

/**
 * Rebuild a postable `Finding` from the durable record of the decision.
 *
 * ⚠️ A `TriageRecord` deliberately does not carry the prose of the review that produced it — it is
 * the decision, and it has to survive a rebase that moves every line in the file (src/triage/store.ts
 * and the same note on `RenderQueueOptions` in src/cli/queue.ts). So the comment carries exactly what
 * the record holds — severity, dimension, title, where it was last seen, and who accepted it — and
 * nothing is invented to fill the gap. A fabricated "how to reproduce" would be worse than a short
 * comment.
 *
 * ⛔ `cls` IS RECOMPUTED FROM THIS PULL REQUEST'S HUNKS, never read back. It is "computed from the
 * diff hunks, never judged" (src/types/finding.ts), and the diff it must be computed against is the
 * one in front of us now: a line that was introduced here last week may be untouched context today.
 * A dimension that has since been switched off yields `shadow`, which is never postable — that is
 * the entire meaning of the class.
 */
export function findingFromRecord(
  record: TriageRecord,
  anchorSet: AnchorSet,
  enabledDimensions: ReadonlySet<string>,
): Finding {
  const cls: FindingClass = !enabledDimensions.has(record.dimensionId)
    ? "shadow"
    : wasIntroducedHere(anchorSet, record.lastAnchor)
      ? "introduced"
      : "pre_existing"

  return {
    fingerprint: record.fingerprint,
    dimensionId: record.dimensionId,
    severity: record.severity,
    cls,
    title: record.title,
    anchor: record.lastAnchor,
    // Empty rather than invented. `commentBody` omits every one of these when it is empty, so the
    // posted comment says only what somebody actually established.
    quote: "",
    consequence: provenanceLine(record),
    failureScenario: "",
    recommendedFix: "",
    effort: "M",
    reasoning: "",
    // ⚠️ Never rendered into a comment — `commentBody` reads none of these. They are false because
    // the record does not carry the evidence from the run that raised it, and claiming six passed
    // checks we cannot see would be the failure src/cli/queue.ts refuses by name.
    components: {
      citedLineExists: false,
      quoteIsByteIdentical: false,
      introducedHere: cls === "introduced",
      survivedRefutation: false,
      hasFailureScenario: false,
      ruleCitationChecksOut: false,
    },
    provenance: {
      backend: "anthropic_api",
      model: "",
      effort: "",
      reviewedSha: record.lastSeenSha,
      usedFallbackRef: false,
    },
  }
}

/** Who decided this, and when. The one sentence a pull-request author needs to place the comment. */
function provenanceLine(record: TriageRecord): string {
  const seen = `raised by the \`${record.dimensionId}\` dimension in ${record.timesSeen} review${record.timesSeen === 1 ? "" : "s"}`
  if (record.state.kind !== "accepted") return `${seen}; not accepted in triage.`
  return `Accepted in triage by ${record.state.by} on ${record.state.at.slice(0, 10)} — ${seen}.`
}

// ── What configuration held back, said out loud ────────────────────────────────────────────────

export interface WithheldFinding {
  readonly fingerprint: string
  readonly path: string
  readonly line: number
  readonly reason: string
}

/**
 * Accepted findings that never became comment candidates, with the reason for each.
 *
 * ⚠️ These never appear in `ReviewPlan.excluded`, because `buildReview` filters them BEFORE it
 * partitions by anchor — they were never comment candidates. That is correct for the builder and
 * wrong for a person, who accepted them and would otherwise watch them vanish. This restates the
 * builder's rule rather than deciding anything; the reconciliation line below is what catches the
 * two drifting apart.
 *
 * ⛔ THE ANCHOR IS TESTED FIRST, BEFORE THE CLASS. A record whose line is not in the current diff at
 * all also fails the `introducedHere` test, so it arrives here as `pre_existing` — and reporting
 * "this pull request did not change that line" for a finding whose line does not EXIST in this diff
 * sends the reader to look at code that is not there. The anchor is also the harder fact: no
 * configuration change would let that comment be posted, because GitHub would 422 the whole review.
 */
export function withheldByConfiguration(
  cfg: PostConfig,
  items: readonly PostableFinding[],
  anchorSet: AnchorSet,
): WithheldFinding[] {
  const out: WithheldFinding[] = []
  for (const { finding: f } of items) {
    const where = { fingerprint: f.fingerprint, path: f.anchor.path, line: f.anchor.line }
    if (!isAnchorable(anchorSet, f.anchor)) {
      out.push({
        ...where,
        reason:
          "the cited line is not in this pull request's diff — the head has moved, or the finding was raised somewhere else; a comment there would fail the entire review with a 422",
      })
      continue
    }
    if (f.cls === "shadow") {
      out.push({
        ...where,
        reason: `the \`${f.dimensionId}\` dimension is switched off, so this finding is visible in triage and never postable`,
      })
      continue
    }
    if (f.cls === "pre_existing" && !cfg.severity.preExisting.comment) {
      out.push({
        ...where,
        reason:
          "pre-existing: this pull request did not change the cited line, and severity.pre_existing.comment is false",
      })
      continue
    }
    if (!cfg.severity.actions[f.severity].comment) {
      out.push({ ...where, reason: `severity.actions.${f.severity}.comment is false` })
    }
  }
  return out
}

// ── Rendering ──────────────────────────────────────────────────────────────────────────────────

const WIDTH = 78

function rule(label: string): string {
  const head = `── ${label} `
  return head.length >= WIDTH ? head : head + "─".repeat(WIDTH - head.length)
}

/** Where a comment lands, in the terms GitHub's API uses for it. */
function spanOf(c: ReviewPlan["body"]["comments"][number]): string {
  return c.start_line === undefined
    ? `${c.path}:${c.line} ${c.side}`
    : `${c.path}:${c.start_line}-${c.line} ${c.start_side ?? c.side}→${c.side}`
}

export function commitStatusLine(cfg: PostConfig, state: string, sha: string): string {
  if (!cfg.post.commitStatus.enabled) {
    return "not set — post.commit_status.enabled is false"
  }
  if (state === "none") {
    // ⛔ A broken or silent reviewer must not mark somebody's build. `none` is a configured
    // instruction, not a gap.
    return "none — deliberately left unset, so this run does not mark the author's build"
  }
  const target = cfg.post.commitStatus.targetUrlBase
  return `${state} · context ${cfg.post.commitStatus.context} · on ${sha}${target === "" ? "" : ` · ${target}`}`
}

/**
 * The review, in full, exactly as it would go on the wire.
 *
 * ⛔ THE REAL BODIES, NEVER A COUNT. A dry run whose output is "would post 4 comments" cannot be
 * checked by the person whose job it is to check it, which makes the second lock decorative.
 */
export function renderPlan(
  cfg: PostConfig,
  plan: ReviewPlan,
  where: { repo: string; pr: number },
): string {
  const n = plan.body.comments.length
  const lines: string[] = [
    rule(`POST /repos/${where.repo}/pulls/${where.pr}/reviews`),
    `commit_id ${plan.body.commit_id} · event ${plan.body.event} · ${n} comment${n === 1 ? "" : "s"}`,
    "",
    rule("summary"),
    plan.body.body,
  ]
  plan.body.comments.forEach((c, i) => {
    lines.push("", rule(`comment ${i + 1} of ${n} — ${spanOf(c)}`), c.body)
  })
  lines.push("", rule("commit status"), commitStatusLine(cfg, plan.commitStatus, plan.body.commit_id))
  return lines.join("\n")
}

// ── The command ────────────────────────────────────────────────────────────────────────────────

function flagValue(argv: readonly string[], name: string): string | undefined {
  const i = argv.indexOf(name)
  return i === -1 ? undefined : argv[i + 1]
}

function countBy(items: readonly PostableFinding[], kind: string): number {
  return items.filter((i) => i.state.kind === kind).length
}

export async function runPostCommand(opts: PostCommandOptions): Promise<number> {
  const env = opts.env ?? process.env
  const out = opts.out ?? ((s: string) => process.stdout.write(s))
  const err = opts.err ?? ((s: string) => process.stderr.write(s))
  const cfg = opts.config
  const argv = opts.argv ?? []

  const repo = opts.repo ?? cfg.repo.slug
  if (repo === "") {
    err("⛔ no repository. Pass `--repo owner/name` or set `slug` under [repo].\n")
    return EXIT.USAGE
  }

  const keys = cfg.secrets.keys.filter((k) => k.includes("GITHUB") || k.includes("GH_"))
  const token = credentialFrom(keys, env)
  if (token === undefined) {
    err(`⛔ no GitHub token. Set one of ${keys.join(", ")}.\n`)
    return EXIT.UNAVAILABLE
  }

  // Licence gate (src/licence/gate.ts): posting to a private repository needs a key, as reviewing does.
  const refusal = await licenceRefusal(repo, token.value, env, opts.fetchImpl === undefined ? {} : { fetchFn: opts.fetchImpl })
  if (refusal !== undefined) {
    err(`${refusal}\n`)
    return EXIT.CONFIG
  }

  // ⚠️ `--dry-run` is the flag form of `post.dry_run` and engages the SAME lock, so a flagged run and
  // a configured one produce byte-identical output. A second, parallel "preview" path is how the two
  // come to disagree about what would be sent.
  const dryRun = cfg.post.dryRun || argv.includes("--dry-run")
  const confirm = flagValue(argv, "--confirm")
  const postCfg: PostConfig = {
    post: { ...cfg.post, dryRun },
    triage: cfg.triage,
    comments: cfg.comments,
    severity: cfg.severity,
  }

  const github = new GitHub({
    token: token.value,
    ...(opts.fetchImpl === undefined ? {} : { fetchImpl: opts.fetchImpl }),
  })

  let head: string
  let files: ChangedFile[]
  try {
    // ⛔ Reads only. Rebuilding the anchor set from the pull request as it stands now is the whole
    // reason this is done here rather than trusted from the review that produced the findings.
    const pull = await github.pullRequest(repo, opts.pr)
    head = pull.headSha
    files = await github.changedFiles(repo, opts.pr)
  } catch (e) {
    err(`⛔ could not read ${repo}#${opts.pr}: ${e instanceof Error ? e.message : String(e)}\n`)
    return EXIT.UNAVAILABLE
  }
  const anchorSet = buildAnchorSet(files)

  const store = await (opts.openStore ?? ((path: string) => openTriageStore({ path })))(cfg.triage.db)
  try {
    err(`triage: ${store.backend.kind} at ${store.backend.path} — ${store.backend.reason}\n`)

    const touched = new Set(files.map((f) => f.path))
    const enabled = new Set(cfg.dimensions.filter((d) => d.enabled).map((d) => d.id))
    // The queue is global and keyed on a fingerprint, not on a pull request. What belongs to THIS
    // post is what was last seen in a file this pull request touches.
    const candidates: PostableFinding[] = store
      .all()
      .filter((r) => touched.has(r.lastAnchor.path))
      .map((r) => ({ finding: findingFromRecord(r, anchorSet, enabled), state: r.state }))
    // ⛔ WHAT MAY REACH GITHUB DEPENDS ON WHETHER A PERSON IS IN THE LOOP, AND IT IS NOT ALWAYS.
    //
    // This used to be `state.kind === "accepted"`, unconditionally. That is correct when
    // `triage.require_human` is on: a `todo` record has been decided by nobody. It is WRONG when it
    // is off — the locks clear, the command reports success, and it posts an EMPTY review, because
    // nothing was ever accepted and nothing ever would be. A tool that says it posted and posted
    // nothing is the failure this repository is organised against.
    //
    // ⭐ Off is the shape the product is actually for: a button in GitHub asks for a review, the
    // review appears on the pull request, and having been reviewed is what satisfies the gate.
    // Triage is the opt-in for someone who wants to curate first, not the only road.
    //
    // Dismissed is excluded either way. A person answered that one, and the answer was no.
    const postable = postCfg.triage.requireHuman
      ? candidates.filter((i) => i.state.kind === "accepted")
      : candidates.filter((i) => i.state.kind !== "dismissed")

    out(`sibyl post — ${repo}#${opts.pr}, head ${head.slice(0, 12)}\n`)
    out(
      `${candidates.length} finding${candidates.length === 1 ? "" : "s"} in the ${files.length} file${files.length === 1 ? "" : "s"} this pull request touches: ` +
        `${postable.length} will be posted (${countBy(candidates, "accepted")} accepted, ${countBy(candidates, "todo")} awaiting a decision, ${countBy(candidates, "dismissed")} dismissed; ` +
        `the human gate is ${postCfg.triage.requireHuman ? "ON, so only accepted findings post" : "OFF, so everything not dismissed posts"}).\n`,
    )
    for (const s of anchorSet.skipped) {
      // ⛔ Named, never dropped silently: nothing can be anchored in these files at all.
      out(`   no anchors in ${s.path} — ${s.reason}\n`)
    }

    // ⛔ THE LOCKS ARE READ OVER THE WHOLE QUEUE, NOT OVER THE ACCEPTED SUBSET. Filtering first would
    // make `triage.require_human` unable to fire by construction, and an un-triaged finding would
    // pass unmentioned. src/github/post.ts states the intended behaviour: "A run where a person
    // accepted everything posts; a run with one un-triaged finding does not, and says which."
    const locks = engagedLocks(postCfg, candidates, confirm)

    let plan: ReviewPlan
    try {
      plan = buildReview(postCfg, {
        reviewedSha: head,
        summary: summaryFor(postable.length, postCfg.triage.requireHuman),
        anchorSet,
        items: postable,
      })
    } catch (e) {
      if (e instanceof PostError) {
        err(`⛔ ${e.message}\n`)
        return EXIT.CONFIG
      }
      throw e
    }

    if (locks.length > 0) out(renderLocks(locks))
    out(renderExclusions(postCfg, plan, postable, anchorSet))

    if (locks.length > 0) {
      out(`\n${renderPlan(postCfg, plan, { repo, pr: opts.pr })}\n`)
      out("\nNothing was sent. Release the locks above and run this again.\n")
      // ⛔ A refusal is a correct outcome, not a failure. The exit code answers "did the harness
      // work", and it did.
      return EXIT.OK
    }

    const result = await postReview(postCfg, {
      repo,
      pullNumber: opts.pr,
      token: token.value,
      ...(opts.fetchImpl === undefined ? {} : { fetchImpl: opts.fetchImpl }),
      reviewedSha: head,
      summary: summaryFor(postable.length, postCfg.triage.requireHuman),
      anchorSet,
      // ⛔ Defence in depth. Even with every lock released, the request body is built from the
      // selected findings and from nothing else — and a dismissed finding is in neither selection.
      items: postable,
      ...(confirm === undefined ? {} : { confirm }),
    })

    if (result.kind === "posted") {
      for (const c of result.plan.body.comments) out(`   ${spanOf(c)}\n`)
      out(
        `\nposted ${result.plan.body.comments.length} comment${result.plan.body.comments.length === 1 ? "" : "s"} in 1 review · ${result.contentRequests} content-creating request${result.contentRequests === 1 ? "" : "s"}\n`,
      )
      // ⚠️ Assembled, not returned: the API's `html_url` is not carried on `PostResult`. A review id
      // of 0 means GitHub accepted the review and returned no id, which is worth saying rather than
      // printing a link that goes nowhere.
      out(
        result.reviewId === 0
          ? "review: created, but GitHub returned no review id\n"
          : `review: https://github.com/${repo}/pull/${opts.pr}#pullrequestreview-${result.reviewId}\n`,
      )
      out(`commit status: ${commitStatusLine(postCfg, result.commitStatus, head)}\n`)
      // ⛔ A blocking review is a SUCCESSFUL run. Exit 0.
      return EXIT.OK
    }

    if (result.kind === "nothing_to_post") {
      out("\nno review was created — nothing was accepted and comments.summary_on_empty is false.\n")
      out(`commit status: ${commitStatusLine(postCfg, result.commitStatus, head)}\n`)
      return EXIT.OK
    }

    if (result.kind === "aborted") {
      out(`\n⛔ nothing was sent: ${result.reason}.\n`)
      out(`   reviewed ${result.expectedSha.slice(0, 12)}, the head is now ${result.actualSha.slice(0, 12)}\n`)
      out("   run `sibyl review` against the new head, triage it, and post that.\n")
      // The harness worked and correctly declined. Not an error.
      return EXIT.OK
    }

    if (result.kind === "refused") {
      out(renderLocks(result.locks))
      out("\nNothing was sent.\n")
      return EXIT.OK
    }

    // ⛔ The only non-zero exit: the harness broke. Nothing about the findings can produce this.
    err(`⛔ the post failed: ${result.error}\n`)
    err(`   commit status: ${commitStatusLine(postCfg, result.commitStatus, head)}\n`)
    return EXIT.UNAVAILABLE
  } finally {
    store.close()
  }
}

/** One honest line. The counts are appended by `renderSummary` in src/github/post.ts. */
/**
 * ⛔ THE SUMMARY MUST NOT CLAIM SOMETHING NOBODY DID. This said "N findings were accepted by a person
 * in triage before anything was posted" unconditionally, and on 2026-09-15 it said exactly that on a
 * real pull request where the human gate was off and nobody had accepted anything. A false sentence
 * about provenance is worse than no sentence: it invites a reader to trust the findings for a reason
 * that does not exist. What is said now depends on which gate actually ran.
 */
function summaryFor(count: number, requireHuman: boolean): string {
  if (count === 0) {
    return requireHuman
      ? "sibyl reviewed this pull request. Nothing is outstanding in triage."
      : "sibyl reviewed this pull request and found nothing to report."
  }
  const findings = `${count} finding${count === 1 ? "" : "s"}`
  return requireHuman
    ? `sibyl reviewed this pull request. ${findings} ${count === 1 ? "was" : "were"} accepted by a person in triage before anything was posted.`
    : `sibyl reviewed this pull request and raised ${findings}. Each one was checked against the code at this commit before it was posted; no person has triaged them yet.`
}

function renderLocks(locks: readonly EngagedLock[]): string {
  const lines = [
    "",
    rule(`${locks.length} lock${locks.length === 1 ? "" : "s"} engaged — nothing will be sent`),
  ]
  for (const l of locks) {
    lines.push(`⛔ ${l.id} — ${l.detail}`)
    lines.push(`   release: ${LOCK_RELEASES[l.id]}`)
  }
  return `${lines.join("\n")}\n`
}

/**
 * ⛔ Every accepted finding that is not in the review, named with the reason.
 *
 * The last line is the reconciliation. It exists because two of the three reasons a finding can be
 * missing are computed in different files, and a count that does not balance is the only way to
 * notice that a fourth reason has appeared.
 */
function renderExclusions(
  cfg: PostConfig,
  plan: ReviewPlan,
  accepted: readonly PostableFinding[],
  anchorSet: AnchorSet,
): string {
  // ⛔ A finding the plan already named is not named twice. Counting it in both lists would make the
  // reconciliation balance while one finding was reported and another had vanished.
  const alreadyNamed = new Set(plan.excluded.map((e) => e.fingerprint))
  const withheld = withheldByConfiguration(
    cfg,
    accepted.filter((i) => !alreadyNamed.has(i.finding.fingerprint)),
    anchorSet,
  )
  const posted = plan.body.comments.length
  const lines: string[] = []

  if (plan.excluded.length > 0 || withheld.length > 0) {
    lines.push("", rule("accepted, and not in this review"))
    for (const e of plan.excluded) {
      lines.push(`   ${shortFingerprint(e.fingerprint)}  ${e.path}:${e.line} — ${e.reason}`)
    }
    for (const w of withheld) {
      lines.push(`   ${shortFingerprint(w.fingerprint)}  ${w.path}:${w.line} — ${w.reason}`)
    }
    lines.push("   Each is still in the queue and is unchanged by this run.")
  }

  lines.push(
    "",
    `${accepted.length} accepted = ${posted} posted + ${plan.excluded.length} excluded + ${withheld.length} withheld by configuration.`,
  )
  const unaccounted = accepted.length - posted - plan.excluded.length - withheld.length
  if (unaccounted !== 0) {
    // ⛔ Louder than the numbers above, because a finding a person accepted has gone somewhere this
    // report cannot name, and that is exactly the failure the report exists to prevent.
    lines.push(
      `⛔ ${Math.abs(unaccounted)} accepted finding${Math.abs(unaccounted) === 1 ? " is" : "s are"} unaccounted for — this report and src/github/post.ts disagree about what was sent.`,
    )
  }
  return `${lines.join("\n")}\n`
}
